import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, FormProvider } from '@tq/ui-components/dist/components/forms-engine';
import {
  Box,
  Button,
  Card,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Select,
  Stack,
  useBanner,
} from '@tq/ui-components';

import { useConsulteeTeams, UpdateAdminUserRequest, usePermissions } from 'api';
import { MultiSelect } from 'components';
import { ApplicationUser, ValueLabelResponse } from 'types';

import { useManageTeamRow } from '../hooks';
import { BANNER_TIMEOUT } from '../../../config';

interface ManageTeamRowProps {
  data: ApplicationUser;
  isAdmin?: boolean;
  setEditable: (editable: boolean) => void;
}

export const EditTeamRow: React.FC<ManageTeamRowProps> = ({
  data,
  isAdmin = false,
  setEditable,
}) => {
  const { data: teamOptions } = useConsulteeTeams();
  const { t } = useTranslation('manage_team');
  const banner = useBanner();
  const { data: permissionData } = usePermissions();

  const [isRegistered, setIsRegistered] = React.useState(data.isRegistered);
  const [canSubmit, setCanSubmit] = React.useState(false);
  const [teams, setTeams] = React.useState<ValueLabelResponse[]>(data?.teams);
  const [userName, setUserName] = React.useState<string>(data?.name);
  const [permission, setPermission] = React.useState<number>(data?.permission?.value);

  const { updateUser, isUpdatingUser } = useManageTeamRow();

  useEffect(() => {
    const sortNumbers = (a: number, b: number) => a - b;

    const permissionChanged = data?.permission?.value !== permission;
    const existingTeamValues = data?.teams?.map((r: ValueLabelResponse) => r.value);
    const selectedTeamValues = teams?.map((u) => u.value);
    const acceptedInviteChanged = data?.isRegistered !== isRegistered;
    const teamChanged =
      existingTeamValues?.slice().sort(sortNumbers).toString() !==
      selectedTeamValues?.slice().sort(sortNumbers).toString();
    const userNameChanged = userName !== data.name;

    const hasChanged =
      permissionChanged || teamChanged || userNameChanged || acceptedInviteChanged;

    setCanSubmit(isAdmin ? hasChanged : hasChanged && permission !== 0 && !!teams.length);
  }, [data, userName, teams, permission, isAdmin, isRegistered]);

  const onAddTeam = (selectedValue: string) => {
    const selectedTeam = teamOptions?.teams.find((r) => r.value === Number(selectedValue));

    if (selectedTeam && !teams.map((o) => o.label).includes(selectedTeam.label)) {
      setTeams((existingTeams) => [...existingTeams, selectedTeam]);
    }
  };

  const onRemoveTeam = (selectedValue: number | string) => {
    const teamToRemove = teams.find((r) => r.value === selectedValue);
    setTeams((existingTeams) => existingTeams.filter((v) => v.value !== teamToRemove?.value));
  };

  const onUpdateUser = () => {
    const user = {
      id: data.id as number,
      permissionId: Number(permission),
      teams: teams?.map((teamDetail) => teamDetail.value) ?? [],
      userName,
      isRegistered: isRegistered ?? false,
    } as UpdateAdminUserRequest;
    updateUser(isAdmin, user, showSuccessBanner, showFailedBanner);
  };

  const showSuccessBanner = () => {
    setEditable(false);
    banner({
      id: 'update-consultee-banner',
      title: t('user_updated', 'User updated'),
      duration: BANNER_TIMEOUT,
    });
  };

  function showFailedBanner() {
    banner({
      id: 'update-consultee-banner-fail',
      title: t('error_updating_user', 'User cannot be updated. Please try again'),
      duration: BANNER_TIMEOUT,
      status: 'error',
    });
  }

  return (
    <Card p={6} border='0' backgroundColor='#EBF7FF'>
      <FormProvider>
        <Form id='manage-consultee-update-form'>
          <Flex direction={{ base: 'column', lg: 'row' }} justifyContent='space-between'>
            <Stack spacing={4} width='100%' direction={{ base: 'column', lg: 'row' }}>
              <FormControl width='100%'>
                <FormLabel htmlFor='name'>{t('name_label', 'Name')}:</FormLabel>
                <Input
                  id='name'
                  type='text'
                  width='100%'
                  defaultValue={data.name}
                  label={t('name_label', 'Name')}
                  mb={0}
                  mode='onBlur'
                  placeholder={t('name_placeholder', 'Name')}
                  onChange={(event) => setUserName(event.target.value)}
                />
              </FormControl>
              <FormControl width='100%'>
                <FormLabel htmlFor='email'>{t('email_label', 'Email')}:</FormLabel>
                <Input
                  id='email'
                  type='email'
                  width='100%'
                  defaultValue={data.email}
                  label={t('email_label', 'Email')}
                  isDisabled
                  mb={0}
                  mode='onBlur'
                  placeholder={t('email_placeholder', 'User email address')}
                />
              </FormControl>
              {!isAdmin && (
                <>
                  <FormControl width='100%'>
                    <MultiSelect
                      fieldId='teams'
                      label={`${t('teams_label', 'Team(s)')}:`}
                      items={teamOptions?.teams}
                      selectedItems={teams}
                      handleSelect={onAddTeam}
                      handleRemoveSelection={onRemoveTeam}
                    />
                  </FormControl>
                  <FormControl width='100%'>
                    <FormLabel htmlFor='permission-edit'>
                      {t('permission_label', 'Permissions')}:
                    </FormLabel>
                    <Select
                      value={permission}
                      onChange={(value) => setPermission(Number(value))}
                      aria-label='Permission select dropdown'
                    >
                      {permissionData?.permissions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                </>
              )}
              <FormControl width='100%'>
                <FormLabel>{t('registered_label', 'Invitation accepted')}:</FormLabel>
                <Checkbox
                  data-testid='invitation-check'
                  id='registered'
                  width='100%'
                  isChecked={isRegistered}
                  label={t('registered_label', 'Invitation accepted')}
                  isDisabled={!isAdmin}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setIsRegistered(event.target.checked)
                  }
                  mb={0}
                />
              </FormControl>
            </Stack>
            <Box width={15} height={8} />
            <FormControl>
              <Button
                mt={{ base: 0, lg: '2.35rem' }}
                isFullWidth
                maxWidth={{ base: '100%', lg: '245px' }}
                isDisabled={!canSubmit}
                isLoading={isUpdatingUser}
                onClick={onUpdateUser}
                px={8}
                py={2}
              >
                {t('update_button_text', 'Update')}
              </Button>
              <Button
                mt={{ base: '0.75rem', lg: '1rem' }}
                isFullWidth
                variant='secondary'
                maxWidth={{ base: '100%', lg: '245px' }}
                onClick={() => {
                  setEditable(false);
                }}
                px={8}
                py={2}
              >
                {t('cancel_button_text', 'Cancel')}
              </Button>
            </FormControl>
          </Flex>
        </Form>
      </FormProvider>
    </Card>
  );
};
